import type { LucideIcon } from "lucide-react";
import { Construction } from "lucide-react";

interface PlaceholderPageProps {
  /** Page title. */
  title: string;
  /** One-line description of what this feature will eventually do. */
  description: string;
  /** Icon to display in the "coming soon" callout. Defaults to Construction. */
  icon?: LucideIcon;
  /** Bullet list of planned capabilities. Optional. */
  plannedCapabilities?: string[];
}

/** Shared placeholder used by every not-yet-implemented feature. Establishes
 *  the route + nav entry + permissions so we can route+gate now and fill in
 *  the real UI later. */
export function PlaceholderPage({
  title,
  description,
  icon: Icon = Construction,
  plannedCapabilities,
}: PlaceholderPageProps) {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
        <Icon className="mx-auto h-10 w-10 text-muted-foreground/40" strokeWidth={1.5} />
        <h2 className="mt-3 text-lg font-semibold">Coming soon</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Feature is scaffolded with <code className="rounded bg-muted px-1">read</code> and{" "}
          <code className="rounded bg-muted px-1">write</code> permissions and a placeholder
          route. Implementation pending.
        </p>
      </div>

      {plannedCapabilities && plannedCapabilities.length > 0 && (
        <div className="rounded-lg border bg-card p-5">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Planned capabilities
          </h3>
          <ul className="space-y-1.5 text-sm">
            {plannedCapabilities.map((cap, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
                <span>{cap}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
